import Vue from 'vue';
import { formatTimestampToHumanDate, isSelectedConversation, unknowTomessage } from '@/vuex-store';
import { v4 as uuid } from 'uuid';
import notificationSound from '@/services/notificationSound';
import desktopNotification from '@/services/desktopNotification';
import i18n from '@/plugins/i18n';

export default {
  namespaced: true,
  state: {
    channels: {},
    clientId: ''
  },
  mutations: {
    SET_CHANNEL_WEBSOCKET(state, { channel, websocket }) {
      Vue.set(state.channels, channel, { websocket });
    },
    SET_CHANNEL_WEBSOCKET_V2(state, { websocket }) {
      Vue.set(state, 'channels', { websocket });
    },
    SET_CLIENT_ID(state, data) {
      Vue.set(state, 'clientId', data);
    },
  },
  actions: {
    initializeWebSocketForChannel({ commit, state, dispatch, rootState }, channel) {
      const ws = new WebSocket(process.env.WEBSOCKET_URL)
      setInterval(() => {
          state.channels[channel].websocket.send(JSON.stringify({data : {clientUid : `platform.pageid.${channel}`}, event : `ping`}))
      }, 58000);
      
      ws.onopen = () =>  dispatch('sendMessageToChannel', {channel, message : {} , event : `subscribe`})
     
      ws.onmessage = (e) => {
        const {data, event} = JSON.parse(e.data)

        if(event === 'instant.message') {
          instantMessageEvent(data, rootState, dispatch)
        }

        if(event === 'complete.message') {
          completeMessageEvent(data, rootState, dispatch)
        }
       
        if(event === 'sended.message') {
          sendedMessageEvent(data, rootState, dispatch)
        }

        if(event === 'read.chat') {
          readChatEvent(data, rootState, dispatch)
        }
      }

      ws.onclose = event => {
        // console.error(`websocket ${channel} closed`)
        dispatch('initializeWebSocketForChannel', channel)
      }

      commit('SET_CHANNEL_WEBSOCKET', { channel, websocket: ws })
    },
    sendMessageToChannel({ state }, { channel, message, event }) {
      if (state.channels[channel] && state.channels[channel].websocket) {
        const data = {
          clientUid : `platform.pageid.${channel}`,
          message,
        }
        state.channels[channel].websocket.send(JSON.stringify({data, event}))
      }
    },
    initializeWebSocketChannels({ commit, state, dispatch, rootState }, { pageIds : pageIds, userId : userId, merchantId : merchantId }) {
      
      const ws = new WebSocket(process.env.WEBSOCKET_URL);
      
      commit('SET_CLIENT_ID', uuid.v4());

      ws.onopen = () => {
        if (state.channels && state.channels.websocket) {
          const data = {
            page_ids: pageIds,
            client_id: state.clientId,
            user_id: userId,
            merchant_id: merchantId,
            message: '',
          }
          state.channels.websocket.send(JSON.stringify({data, event: 'subscribe'}))
        }
      }
     
      ws.onmessage = (e) => {
        const {data, event} = JSON.parse(e.data)

        if(event === 'message.instant') {
          instantMessageEvent(data, rootState, dispatch)
        }

        if(event === 'message.complete') {
          completeMessageEvent(data, rootState, dispatch)
        }

        if(event === 'message.typing' || event === 'message.remove.typing') {
          typingMessageEvent({ data, event }, rootState, dispatch)
        }
       
        if(event === 'message.sent') {
          sendedMessageEvent(data, rootState, dispatch)
        }

        if(event === 'message.read') {
          messageReadEvent(data, rootState, dispatch)
        }

        if(event === 'message.edited') {
          messageEditedEvent(data, rootState, dispatch)
        }

        if(event === 'chat.update') {
          chatUpdate(data, rootState, dispatch)
        }

        if(event === 'page.token_expired') {
          pageTokenExpiredEvent(data, rootState, dispatch)
        }

        if(event === 'page.token_near_expire') {
          pageTokenNearExpireEvent(data, rootState, dispatch)
        }

        if(event === 'ping') {
          state.channels.websocket.send(JSON.stringify({data : {
            client_id: state.clientId,
            user_id: userId,
          }, event : `pong`}))
        }
      }

      ws.onclose = event => {
        console.error(`websocket ${state.clientId} closed`)
        setTimeout(() => {
          dispatch('initializeWebSocketChannels', { pageIds : pageIds, userId : userId, merchantId : merchantId })
        }, 5000);
      }
      
      commit('SET_CHANNEL_WEBSOCKET_V2', { websocket: ws })
    },
    sendMessageToChannels({ state }, { data, event }) {
      if (state.channels && state.channels.websocket) {
        data = {
          ...data,
          client_id: state.clientId
        }
        state.channels.websocket.send(JSON.stringify({data, event}))
      }
    },
  }
}

// แอดมินไม่ได้กำลังมองหน้า GoChat จริง = สลับ tab ไป (hidden) หรือ สลับไปแอป/หน้าต่างอื่น (window ไม่ focus)
const isPageInactive = () => document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus());

// ข้อความสำหรับ desktop notification: text ใช้ข้อความจริง, ชนิดอื่นใช้ label ตามชนิด (ผ่าน i18n ตาม locale)
const notificationBody = (data) => {
  const type = data.type;
  // sticker บางแพลตฟอร์มมาผ่าน flag แทน type ตรงๆ (เหมือน logic ใน BlockChat)
  if (type === 'sticker' || data.is_sticker || (data.option && data.option.sticker)) {
    return i18n.t('sent a sticker');
  }
  if (!type || type === 'text') {
    // กัน data.message ที่ไม่ใช่ string (บาง path เป็น object) ไม่ให้โชว์ [object Object]
    return (typeof data.message === 'string' && data.message) ? data.message : i18n.t('sent you a message');
  }
  const labelKeys = {
    image: 'sent a photo',
    file: 'sent a file',
    video: 'sent a video',
    audio: 'sent a voice message',
  };
  return labelKeys[type] ? i18n.t(labelKeys[type]) : i18n.t('sent you a message');
};

const instantMessageEvent = (data, root, dispatch) => {
  data.readed = false;
  data.is_notify = true;
  data.direction = data.from == "page" ? "right" : "left";
  
  const option = {root:true}

  var messageTypeOption = {
    emoticon: data.is_has_emoji ? true : false,
    like: data.message.is_like ? true : false,
    sticker: data.message.is_sticker ? true : false,
  }
  // console.log("instant: ", data)

  // --- เสียงแจ้งเตือน + แจ้งเตือนบนเดสก์ท็อป ข้อความลูกค้าเข้า (FTR-2313) ---
  // เฉพาะข้อความจากลูกค้า (from === 'customer') — ต้องอยู่ "ก่อน" early-return ของ isSelectedConversation ด้านล่าง
  // เงื่อนไข enabled/permission/debounce/autoplay-unlock จัดการภายใน service แต่ละตัว
  if (data.from === 'customer') {
    const isViewing = isSelectedConversation(data.customer_id, data.page_id, root.selectedConversationId)
    // เสียง: เล่นเมื่อไม่ได้เปิดห้องนั้นอยู่ หรือ แอดมินไม่ได้มองหน้า GoChat (สลับ tab/หน้าต่าง)
    if (!isViewing || isPageInactive()) {
      notificationSound.play()
    }
    // แจ้งเตือนบนเดสก์ท็อป: เฉพาะตอนแอดมินไม่ได้มองหน้า GoChat (สลับ tab/แอปอื่น)
    if (isPageInactive()) {
      // type-guard: customer_name ต้องเป็น string เท่านั้น (บาง payload อาจไม่ใช่) กัน .trim() throw ทำ event พัง
      const notiTitle = (typeof data.customer_name === 'string' && data.customer_name.trim()) ? data.customer_name : i18n.t('new message from customer')
      desktopNotification.notify(notiTitle, notificationBody(data), 'gochat-' + data.customer_id)
    }
  }
  // --- จบแจ้งเตือน ---

  if (data.from === "page") {
    const { name, logo } = root.channels.find(({ platform_page_id }) => data.page_id == platform_page_id) || {
      name: "",
      logo: "",
    };
    data.send_raw_data = {
      sender_name: name,
      sender_profile: logo,
    };
  }

  let instant = root.anonymousMessage.findIndex(({ message_id }) => message_id == data.message_id);

  //protect case instant after complete :)
  if (instant >= 0) {
    const arr = [...root.anonymousMessage];
    arr.splice(instant, 1);
    dispatch("anonymousMessageAction", arr, option);
    return;
  }

  dispatch("addUnknowMessageAction", data, option);

  const { customer_id, page_id, from } = data;

  var chat = null;
  var conversations = [...root.conversations]

  for (let i = 0; i < conversations.length; i++) {
    if (conversations[i].customer_platform_user_id == customer_id && conversations[i].platform_page_id == page_id) {
      chat = conversations[i]
      conversations[i].sender_id = from == "customer" ? conversations[i].customer_id : conversations[i].sender_id
      conversations[i].unread_message_count = from == "customer" ? conversations[i].unread_message_count + (!data.readed ? 1  : 0) : conversations[i].unread_message_count
      if(data.timestamp < conversations[i].timestamp)  break ;

      conversations[i].message_last = data.message
      conversations[i].message_last_id = data.message_id
      conversations[i].message_last_type = data.type
      conversations[i].option = data.option ? data.option : messageTypeOption
      conversations[i].date = data.date
      conversations[i].timestamp = data.timestamp

      break; 
    }
  }

  if (chat === null) {
    const { name, logo } = root.channels.find((c) => c.platform_page_id == data.page_id) || {
      name: "",
      logo: "",
    };

    chat = {
      id: `chat_unknownymous_${conversations.length + 1}`,
      page_id: data.page_id,
      platform_page_id: data.page_id,
      customer_id: data.customer_id,
      sender_id: data.customer_id,
      unread_message_count: !data.readed ? 1 : 0,
      merchant_id: "",
      integration_name: data.platform,
      page_name: name,
      page_status: "",
      page_is_connected: true,
      platform: data.platform,
      is_confirm_integration: true,
      customer_name: data.customer_name || "",
      customer_platform_user_id: data.customer_id,
      message_id: data.message_id,
      message_last: data.message,
      message_last_type: data.type,
      mark_as_status: "",
      assignee: null,
      order_status: "",
      chat_status: data.unreadCnt ? "UNREAD" : "READ",
      tags: [],
      chat_room_id: "",
      isImportant: 0,
      customer_image: "",
      from: data.from,
      page_image: logo,
      date: data.date,
      time: data.timestamp,
      timestamp: data.timestamp,
      platform: data.platform,
      send_raw_data: data.send_raw_data || {},
      is_sticker : data.is_sticker || (data.option && data.option.sticker),
      is_like : data.is_like || (data.option && data.option.like),
      is_has_emoji : data.is_has_emoji || (data.option && data.option.emoticon),
    };

    conversations.push(chat);
  }

  dispatch("setConversationsAction", conversations, option);

  if (!isSelectedConversation(data.customer_id,data.page_id, root.selectedConversationId))
    return;

  if (data.type == "file") return;
  // dispatch("setNewElementAction", {...toMessage(data), event: "instant"}, option);
  //set message
  dispatch('setNewElementAction', {
    ...toMessage(data),
    created_at: data.created_at ? data.created_at : data.timestamp ? new Date(data.timestamp).toISOString() : new Date().toISOString(),
    option: data.option ? data.option : messageTypeOption,
    message_id: data.message_id,
    platform_message_id: data.platform === 'shopee' ? data.platform_message_id : undefined,
    customer_name : data.customer_name || "",
    sender_image : data.sender_image,
    sender_id : data.sender_id,
    sender_name : data.sender_name,
    status: data.status || "INCOMPLETE",
    timestamp: data.timestamp,
    event : 'instant',
  }, option);
};

const completeMessageEvent = (data, root, dispatch) => {
  const { chats = null, message, clientUid} = data
  const clientUidFormat = 'platform.pageid.'
  const pageId = clientUid.replace(clientUidFormat, '')
  const option = {root:true}

  let chat = root.conversations.find(p => p.customer_platform_user_id == chats.customer_platform_user_id && p.platform_page_id == chats.platform_page_id) || null
  
  let itemIndex = root.anonymousMessage.findIndex(({message_id}) => message_id == message.platform_message_id)
  let itemCustomerIndex = root.anonymousMessage.findIndex(({customer_id}) => customer_id == message.customer_platform_user_id)
  // console.log(itemIndex, itemCustomerIndex)

  const instant = itemIndex >= 0
  // ห้องแชทนี้เปิดอยู่หรือไม่ — ต้องรู้ตั้งแต่ก่อนแตกเคส เพราะเคส "ไม่มีข้อความ
  // ชั่วคราวให้แทนที่" ต้องตัดสินใจจากตรงนี้ว่าจะสร้างข้อความชั่วคราวไหม
  const selected = isSelectedConversation(chats.customer_platform_user_id, chats.platform_page_id, root.selectedConversationId)

  var unknowMessag = null
  var updateElament = false
  var replacingId = null
  var messageTypeOption = {
    emoticon: data.message && data.message.is_has_emoji ? true : data.chat && data.chat.message_last_is_has_emoji ? true : false,
    like: data.message && data.message.is_like ? true : data.chat && data.chat.message_last_is_like ? true : false,
    sticker: data.message && data.message.is_sticker ? true : data.chat && data.chat.message_last_is_sticker ? true : false,
  }
  // console.log("complete: ", data, root)
  if(instant) {
    unknowMessag = root.anonymousMessage[itemIndex]
    replacingId = unknowMessag.message_id

    if(unknowMessag.type != message.message_type || unknowMessag.message != message.message) {
      updateElament = true

       //clone complete message to instant
      unknowMessag.type = message.message_type
      unknowMessag.option = message.option ? message.option : messageTypeOption
      unknowMessag.message = message.message
      unknowMessag.platform = chats.integration_name
    }

    const arr = [...root.anonymousMessage]
    arr.splice(itemIndex, 1)
    dispatch("anonymousMessageAction", arr, option);
    
  } else if(itemCustomerIndex >= 0) {
    unknowMessag = root.anonymousMessage[itemCustomerIndex]
    replacingId = unknowMessag.message_id

    if(unknowMessag.type != message.message_type || unknowMessag.message != message.message) {
      updateElament = true

       //clone complete message to instant
      unknowMessag.type = message.message_type
      unknowMessag.option = message.option ? message.option : messageTypeOption
      unknowMessag.message = message.message
      unknowMessag.platform = chats.integration_name
    }

    const arr = [...root.anonymousMessage]
    arr.splice(itemCustomerIndex, 1)
    dispatch("anonymousMessageAction", arr, option);
    
  } else {
    updateElament = true

    unknowMessag = {
      "page_id": pageId,
      "customer_id": message.customer_platform_user_id,
      "customer_name": message.customer_name,
      "customer_image": chats.customer_image ? chats.customer_image : "",
      "from": message.customer_id == message.sender_id ? "customer" : 'page',
      "type": message.message_type,
      "message_id": message.platform_message_id,
      "message": message.message,
      "option": message.option ? message.option : messageTypeOption,
      "is_sticker": message.is_sticker || (message.option && message.option.sticker),
      "is_like": message.is_like || (message.option && message.option.like),
      "is_has_emoji": message.is_has_emoji || (message.option && message.option.emoticon),
      "timestamp": message.timestamp,
      "platform": chats.integration_name,
      "date": formatTimestampToHumanDate(message.timestamp, 'date'),
      "time": formatTimestampToHumanDate(message.timestamp, 'time'),
    }
    // ข้อความชั่วคราวใช้สำหรับห้องที่ "ไม่ได้เปิดอยู่" เท่านั้น (ไปขึ้นเป็นข้อความ
    // ล่าสุด/ตัวนับที่ยังไม่อ่านในรายชื่อแชท) — ถ้าห้องเปิดอยู่ ข้อความจริงจะถูกวาด
    // ด้วย setNewElementAction ท้ายฟังก์ชันนี้แล้ว การสร้างอันชั่วคราวซ้อนเข้าไปอีก
    // ทำให้ข้อความเดียวขึ้นสองบับเบิล (ISS-1087)
    //
    // เคสนี้เกิดกับข้อความที่ร้านส่งจากแอพ Instagram เอง เพราะ webhook route ไม่ push
    // message.instant ให้ข้อความ echo จึงไม่มีอันชั่วคราวมาให้แทนที่ แล้วตกมาที่เคสนี้
    //
    // ข้อยกเว้น: ห้องที่ยังไม่รู้ชื่อลูกค้า (customer_name ว่าง) ฟังก์ชันนี้จะ return
    // ออกไปก่อนถึง setNewElementAction เพื่อไปเติมชื่อในรายชื่อแชท ข้อความจริงจึงไม่
    // ถูกวาด — เคสนั้นต้องคงข้อความชั่วคราวไว้ ไม่งั้นข้อความจะหายไปทั้งข้อความ
    const realMessageWillRender = selected && !(chat && chat.customer_name === "")
    if (!realMessageWillRender) dispatch("addUnknowMessageAction", unknowMessag, option);
  }

  if (selected) updateElament = true;

  let firstConversation = false
  // console.log("chats ", chats)
  // console.log("message: ", message)
  //case no conversation in list
  if (chat === null) {
    chats.message_last_is_has_emoji = message.is_has_emoji || (message.option && message.option.emoticon);
    chats.message_last_is_like = message.is_like || (message.option && message.option.sticker);
    chats.message_last_is_sticker = message.is_sticker || (message.option && message.option.sticker);
    chats.option = message.option ? message.option : messageTypeOption
    chats.is_has_emoji = message.is_has_emoji || (message.option && message.option.emoticon);
    chats.is_like = message.is_like || (message.option && message.option.like);
    chats.is_sticker = message.is_sticker || (message.option && message.option.sticker);
    chats.sender_id = message.sender_id;
    chats.last_message_source = (message.raw_data && message.raw_data.source) || null;
    chats.platform = chats.integration_name;
    chats.customer_id = chats.customer_id;
    chats.customer_name = chats.customer_name || "-";
    chats.page_id = chats.page_id;
    chat = chats
    firstConversation = true
    dispatch("setConversationsAction", [...root.conversations, chats], option)
  } 

  // case unknow user got complete mesg
  if(chat.customer_name === "") {
    dispatch("setConversationsAction", [...root.conversations].map(item => {
      if(item.customer_platform_user_id == chats.customer_platform_user_id && item.platform_page_id == chats.platform_page_id) {
    
        let obj = {
          ...chats, 
          customer_name: message.customer_name || "-",
          message_last_is_has_emoji: message.is_has_emoji || (message.option && message.option.emoticon),
          message_last_is_like: message.is_like || (message.option && message.option.like),
          message_last_is_sticker: message.is_sticker || (message.option && message.option.sticker),
          option: message.option ? message.option : messageTypeOption,
          is_has_emoji: message.is_has_emoji || (message.option && message.option.emoticon),
          is_like: message.is_like || (message.option && message.option.like),
          is_sticker: message.is_sticker || (message.option && message.option.sticker),
          sender_id: message.sender_id,
          message_last : item.message_last, 
          message_last_type : item.type,
          unread_message_count : item.unread_message_count,
          customer_image : chats.customer_image || item.customer_image || '',
          platform: chats.integration_name,
        }

        if(message.timestamp > item.timestamp) {

            obj = {
            ...obj,
            message_last : message.message,
            message_last_type : message.type,
            option: message.option ? message.option : messageTypeOption,
            timestamp : message.timestamp,
            date : formatTimestampToHumanDate(message.timestamp, 'date'),
            last_message_source: (message.raw_data && message.raw_data.source) || null,
          }
        }
        
        return obj
      }
      return item
    }), option)

    if(isSelectedConversation(chats.customer_platform_user_id, chats.platform_page_id, root.selectedConversationId)) {
      dispatch("setFirtOfChatAction", chats)
    }
    return 
  } 

  let conversations = [...root.conversations]

  let updateConversations = false 

  for (let i = 0; i < conversations.length; i++) {
    if (conversations[i].customer_platform_user_id == chats.customer_platform_user_id && conversations[i].platform_page_id == chats.platform_page_id) {
      conversations[i].message_last_is_has_emoji = message.message_last_is_has_emoji || (message.option && message.option.emoticon)
      conversations[i].message_last_is_like = message.message_last_is_like || (message.option && message.option.like)
      conversations[i].message_last_is_sticker = message.message_last_is_sticker || (message.option && message.option.sticker)
      conversations[i].option = message.option ? message.option : messageTypeOption
      conversations[i].is_has_emoji = message.is_has_emoji || (message.option && message.option.emoticon)
      conversations[i].is_like = message.is_like || (message.option && message.option.like)
      conversations[i].is_sticker = message.is_sticker || (message.option && message.option.sticker)
      conversations[i].sender_id = message.sender_id
      conversations[i].customer_id = chats.customer_id
      conversations[i].customer_name = chats.customer_name || '-'
      conversations[i].customer_image = chats.customer_image || conversations[i].customer_image || ''
      conversations[i].page_id = chats.page_id
      conversations[i].platform = chats.integration_name

      //is mean all old & new all is same no need to update
      if((message.timestamp || chats.timestamp) < conversations[i].timestamp)  break ;

      const messageIsSame = conversations[i].message_last == message.message
      const messageId = conversations[i].message_last_id == message.platform_message_id
      const messageType = conversations[i].message_last_type == message.message_type

      if(messageIsSame && messageId && messageType) break ;
      
      conversations[i].unread_message_count = message.customer_id == message.sender_id ? conversations[i].unread_message_count + (firstConversation === true ? 0 : +(!instant)) : conversations[i].unread_message_count

      conversations[i].message_last = message.message
      conversations[i].message_last_id = message.platform_message_id
      conversations[i].message_last_type = message.message_type
      conversations[i].last_message_source = (message.raw_data && message.raw_data.source) || null
      conversations[i].timestamp = message.timestamp || chats.timestamp
      conversations[i].date = formatTimestampToHumanDate((message.timestamp || chats.timestamp), 'date')
      
      updateConversations = true

      break; 
    }
  }
  
  // set conversations
  if(updateConversations === true) dispatch("setConversationsAction", conversations, option)

  if(!selected || !updateElament) return
  //set message
  dispatch('setNewElementAction', {
    ...toMessage(unknowMessag),
    _id: message._id,
    created_at: message.created_at ? message.created_at : message.timestamp ? new Date(message.timestamp).toISOString() : new Date().toISOString(),
    option: message.option ? message.option : messageTypeOption,
    message_id: message.raw_data.sentMessages ? message.raw_data.sentMessages[0].id : message.raw_data.message ? message.raw_data.message.mid : "",
    customer_name : message.customer_name || "",
    sender_image : message.sender_image,
    sender_id : message.sender_id,
    sender_name : message.sender_name,
    status: message.status,
    event : 'complete',
    timestamp: message.timestamp,
    last_msg_id: message.platform_message_id,
    platform_message_id: message.platform_message_id, // Forward platform_message_id for duplicate detection (FB/LINE/Shopee)
    replacing_id: replacingId, // Client/instant message_id of optimistic message to replace
    reply_to: message.reply_to, // Forward reply_to for quoted-reply preview rendering (CS-21775)
  }, option);
}

const sendedMessageEvent = (data, root, dispatch) => {
  const { message } = data

  // console.log(data)

  if(!message) return

  const option = {root:true}
  
  dispatch("setConversationsAction", [...root.conversations].map(item => {
    if(item.customer_platform_user_id == message.customer_platform_user_id && item.platform_page_id == message.page_id) {

      let obj = {
        ...item,
        sender_id: message.send_raw_data.sender_id || this.$store.state.auth.user._id,
        message_last_is_has_emoji: message.is_has_emoji || (message.option && message.option.emoticon),
        message_last_is_like: message.is_like || (message.option && message.option.like),
        message_last_is_sticker: message.is_sticker || (message.option && message.option.sticker),
        is_has_emoji: message.is_has_emoji || (message.option && message.option.emoticon),
        is_like: message.is_like || (message.option && message.option.like),
        is_sticker: message.is_sticker || (message.option && message.option.sticker),
        message_type: message.message_type,
        created_at: message.created_at ? message.created_at : message.timestamp ? new Date(message.timestamp).toISOString() : new Date().toISOString(),
      }

      if(message.timestamp > item.timestamp) {
        obj = {
          ...obj,
          message_last : message.message,
          message_last_type : message.message_type,
          timestamp : message.timestamp,
          date : formatTimestampToHumanDate(message.timestamp, 'date')
        }
      }
     
      return obj
    }
    return item
  }), option)

  if(!isSelectedConversation(message.customer_id, message.page_id, root.selectedConversationId)) return
    
  dispatch('setNewElementAction', message, option);
  
}

const readChatEvent = (data, root, dispatch) => {
  const { message } = data

  if(!message) return

  const {customer_id, page_id} = message
  
  const array = [...root.conversations]
  for (let i = 0; i < array.length; i++) {
    if (array[i].customer_id == customer_id && array[i].page_id == page_id) {
      array[i].unread_cnt = 0;
      array[i].unread_message_count = 0;
      break; 
    }
  }

  dispatch("setConversationsAction", array, {root:true})
}

//V2
const typingMessageEvent = (data, root, dispatch) => {

  const option = {root:true}

  // console.log(data)

  dispatch('setMessageTypingAction', data, option);

}

const messageReadEvent = (data, root, dispatch) => {
  const { customer_id, page_id } = data

  if(!customer_id && !page_id) return
  
  const array = [...root.conversations]
  for (let i = 0; i < array.length; i++) {
    if (array[i].customer_id == customer_id && array[i].platform_page_id == page_id) {
      array[i].unread_cnt = 0;
      array[i].unread_message_count = 0;
      break; 
    }
  }

  dispatch("setConversationsAction", array, {root:true})
}

// CS-22992 — ลูกค้าแก้ไขข้อความที่ส่งไปแล้วบน Messenger (แก้ได้ภายใน 15 นาที)
// ไม่ใช่ข้อความใหม่ จึงห้ามเพิ่มบับเบิล/เด้งห้องขึ้นบน/เพิ่ม unread — แค่แก้เนื้อหาของข้อความเดิมในที่
const messageEditedEvent = (data, root, dispatch) => {
  const message = data && data.message
  if (!message || !message._id) return

  // อัปเดตข้อความตัวอย่างในหน้ารายการแชท เฉพาะห้องที่ข้อความนี้ยังเป็นข้อความล่าสุด
  //
  // ระวัง: message_last_id ฝั่ง store เก็บ "platform message id" (mid) ไม่ใช่ Mongo _id
  // — เขียนไว้ 2 ที่คือ wss.js:240 (data.message_id) และ :510 (message.platform_message_id)
  // ต่างจากฝั่ง DB ที่ chat_room.message_last_id เก็บ Mongo _id จึงเทียบทั้งสองแบบกันพลาด
  const conversations = [...root.conversations]
  for (let i = 0; i < conversations.length; i++) {
    const lastId = conversations[i].message_last_id
    const isSameMessage = lastId == message.platform_message_id || lastId == message._id
    if (conversations[i].customer_id == message.customer_id && isSameMessage) {
      conversations[i].message_last = message.message
      conversations[i].message_last_type = message.message_type
      break
    }
  }
  dispatch("setConversationsAction", conversations, {root:true})

  // ให้หน้าจอที่เปิดแชทนี้อยู่แก้บับเบิลเดิม
  dispatch("setEditedMessageAction", message, {root:true})
}

const chatUpdate = (data, root, dispatch) => {

  if(!data) return
  
  const array = [...root.conversations]
  for (let i = 0; i < array.length; i++) {
    if (array[i].customer_id == data.customer_id && array[i].platform_page_id == data.page_id) {
      array[i].assignee = root.assignees.find(assignee => assignee._id == data.new_assignee);
      array[i].assigned_name = root.assignees.find(assignee => assignee._id == data.new_assignee) && root.assignees.find(assignee => assignee._id == data.new_assignee)._id ? root.assignees.find(assignee => assignee._id == data.new_assignee).name : "";
      array[i].assigned_image = root.assignees.find(assignee => assignee._id == data.new_assignee) && root.assignees.find(assignee => assignee._id == data.new_assignee)._id ? root.assignees.find(assignee => assignee._id == data.new_assignee).image : null;
      array[i].mark_as_status = data.new_status;
      array[i].tags = data.tags;
      array[i].customer_info = {
        ...array[i].customer_info,
        full_name: data.customer_info.full_name || '',
        gender: data.customer_info.gender || '',
        email: data.customer_info.email || '',
        phone: data.customer_info.phone || '',
        phone2: data.customer_info.phone2 || '',
        social_name: data.customer_info.social_name || '',
        line_id: data.customer_info.line_id || '',
        address: {
          description: data.customer_info.address.description || '',
          sub_district: data.customer_info.address.sub_district || '',
          district: data.customer_info.address.district || '',
          province: data.customer_info.address.province || '',
          zipcode: data.customer_info.address.zipcode || ''
        },
        billing_address: {
          description: data.customer_info.billing_address.description || '',
          sub_district: data.customer_info.billing_address.sub_district || '',
          district: data.customer_info.billing_address.district || '',
          province: data.customer_info.billing_address.province || '',
          zipcode: data.customer_info.billing_address.zipcode || ''
        },
        updatedAt: data.customer_info.updatedAt,
      };
      break; 
    }
  }

  dispatch("setConversationsAction", array, {root:true})

  dispatch('setChatUpdateAction', data, {root:true});

}

const messageSentEvent = (data, root, dispatch) => {

  const { page_id, customer_id, user_id, message, message_images, timestamp } = data

  if(!page_id && !customer_id && !timestamp && !user_id) return

  const option = {root:true}
  
  dispatch("setConversationsAction", [...root.conversations].map(item => {
    if(item.customer_id == customer_id && item.platform_page_id == page_id) {
      let messageSent = '';
      let messageType = ''

      if (message) {
        messageSent = message;
        messageType = 'text';
      } else if (message_images) {
        messageSent = message_images;
        messageType = 'image';
      }

      let obj = {
        ...item,
        sender_id: user_id,
        message_last_is_has_emoji: false,
        message_last_is_like: false,
        message_last_is_sticker: false,
        is_has_emoji: false,
        is_like: false,
        is_sticker: false,
        message_type: messageType,
      }

      if(timestamp > item.timestamp) {
        obj = {
          ...obj,
          message_last : messageSent,
          message_last_type : messageType,
          timestamp : timestamp,
          date : formatTimestampToHumanDate(timestamp, 'date')
        }
      }
     
      return obj
    }
    return item
  }), option)

  if(!isSelectedConversation(customer_id, page_id, root.selectedConversationId)) return
    
  dispatch('setNewElementAction', message, option);
  
}


const pageTokenExpiredEvent = (data, root, dispatch) => {
  const option = { root: true }

  // Mark the page as disconnected in channels store
  const updatedChannels = root.channels.map(channel => {
    if (channel.platform_page_id === data.page_id) {
      return { ...channel, is_connected: false, page_integrate_status: 'disconnect' }
    }
    return channel
  })
  dispatch('setChannelsAction', updatedChannels, option)

  // Merge เฉพาะเพจใหม่ที่เพิ่งหลุดเข้า itemsPageDisconnected — ไม่แทนที่ทั้ง array
  // (แทนที่ทั้ง array จะทำให้ nearby/expired pages ที่ is_connected=true หายออกจากรายการ)
  const newlyDisconnectedChannel = updatedChannels.find(
    channel => channel.platform_page_id === data.page_id && channel.id !== 'allpage'
  )
  if (newlyDisconnectedChannel) {
    const newEntry = {
      name: newlyDisconnectedChannel.name,
      page_id: newlyDisconnectedChannel.page_id,
      integration_name: newlyDisconnectedChannel.integration_name,
      logo: newlyDisconnectedChannel.logo || null,
    }
    const existingItems = root.itemsPageDisconnected || []
    const alreadyExists = existingItems.some(item => item.page_id === newEntry.page_id)
    if (!alreadyExists) {
      dispatch('setPageDisconnectedAction', [...existingItems, newEntry], option)
    }
  }
  // Reset showedPageDisconnected ให้ modal ขึ้นได้อีกครั้งสำหรับ event ใหม่ที่เข้ามา
  dispatch('setShowedPageDisconnectedAction', false, option)
}

const pageTokenNearExpireEvent = (data, root, dispatch) => {
  const option = { root: true }
  // อัปเดต access_token_expire_date + page_integrate_status ใน channels store
  const updatedChannels = root.channels.map(channel => {
    if (channel.platform_page_id === data.page_id) {
      return { ...channel, access_token_expire_date: data.access_token_expire_date, page_integrate_status: 'nearby' }
    }
    return channel
  })
  dispatch('setChannelsAction', updatedChannels, option)

  // Merge เพจที่ใกล้หมดอายุเข้า itemsPageDisconnected เพื่อให้ modal แสดงเตือน
  const nearbyChannel = updatedChannels.find(
    channel => channel.platform_page_id === data.page_id && channel.id !== 'allpage'
  )
  if (nearbyChannel) {
    const newEntry = {
      name: nearbyChannel.name,
      page_id: nearbyChannel.page_id,
      integration_name: nearbyChannel.integration_name,
      logo: nearbyChannel.logo || null,
    }
    const existingItems = root.itemsPageDisconnected || []
    const alreadyExists = existingItems.some(item => item.page_id === newEntry.page_id)
    if (!alreadyExists) {
      dispatch('setPageDisconnectedAction', [...existingItems, newEntry], option)
      dispatch('setShowedPageDisconnectedAction', false, option)
    }
  }
}

const toMessage = (data) => {
  const time = `${data.date} ${data.time}`

  return {
    "_id": data.message_id,
    "page_id": data.page_id,
    "message_type": data.type,
    "message": data.message,
    "customer_id": data.customer_id,
    "customer_platform_user_id": data.customer_id,
    "sender_id": data.customer_id,
    "is_notify": true,
    "status": "ACTIVE",
    "customer_name": data.customer_name || "",
    "sender_name": "",
    "sender_image": "",
    "datetime": time,
    "datetime_sort": time,
    "is_sticker": data.is_sticker || (data.option && data.option.sticker),
    "is_like": data.is_like || (data.option && data.option.like),
    "is_has_emoji": data.is_has_emoji || (data.option && data.option.emoticon),
    "sticker_type": "",
    "integration_name": data.integration_name || data.platform,
    "timestamp": data.timestamp,
    "from": data.from
  }
}



// WEBPACK FOOTER //
// ./src/vuex-store/modules/wss.js